// feature/role-views — abogado-only two-column case detail (SPEC §9.5).
// Wider slide-in (1100px). On viewports <1200px, falls back to a tabbed
// single column so the layout stays usable on a narrow window or iPad portrait.

import React, { useEffect, useMemo, useRef, useState } from "react";
// Sprint 56b — CenteredModal everywhere. The width override below
// continues to force the wider 1100px layout for the abogado view.
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import Confirm from "../../components/Confirm";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill } from "../../components/Pills";
import { cases as casesApi, insumos } from "../../api/endpoints";
import { apiBase } from "../../api/client";
import { useToast } from "../../components/Toast";
import { useAuth } from "../../auth/AuthContext";
import { TERMINO_OPTIONS, formatTermino } from "../../utils/termino";
// feature/lifecycle-redesign. LawyerActionPanel replaces the
// Sprint 54 card that wrapped MarcarContestada + SolicitarModeracion.
// It surfaces the Esperando insumo button + Timer 1/2 banners + the
// Sprint 56 centered-modal Contestar flow, and self-gates per state.
import LawyerActionPanel from "../../components/lawyer/LawyerActionPanel";
// sprint83.2 — 3 mandatory LITISOFT dropdowns rendered inline in the
// case detail view so abogados can fill them without leaving their
// workflow. The "Datos LITISOFT obligatorios" section anchors the
// scroll target the validation modal jumps to.
import LitisoftMandatoryInline from "../../components/LitisoftMandatoryInline";
// feature/carpetas-proceso — Phase 1. Same dedup banner the carpetas
// team sees, surfaced to abogados too: knowing a sibling actuación on
// the same proceso has already been billed lets them flag the case to
// the carpetas team instead of re-doing work.
import ProcesoCarpetasBanner from "../../components/ProcesoCarpetasBanner";
// feature/carpetas-phase2-docs — P2 + P2.2.
// Uploaders see the full per-slot panel; non-uploaders (pure abogados)
// see the compact CarpetaStatusBadge in the header instead.
import CarpetaDocumentsPanel from "../../components/CarpetaDocumentsPanel";
import CarpetaStatusBadge from "../../components/CarpetaStatusBadge";
// feature/inline-accionante-edit — case-detail surface for the
// existing PATCH /api/personas/:id endpoint.
import InlineEditAccionante from "../../components/InlineEditAccionante";
// feature/tipo-fallo-edit — modal + role gate for tipo_fallo edits
// on fallo cases (small dd added to InsumosColumn for fallos only).
import TipoFalloEditModal from "../../components/TipoFalloEditModal";
import { PERSONA_EDIT_ROLES } from "../Personas";
import { formatTipo } from "../../utils/case_taxonomy";
import { rolesOf } from "../../utils/permissions";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";
const DOC_TYPES = [
  ["contestacion",     "Contestación"],
  ["memorial",         "Memorial"],
  ["sello_radicacion", "Sello de radicación"],
  ["dictamen_junta",   "Dictamen de junta"],
  ["auto_juzgado",     "Auto del juzgado"],
  ["otro",             "Otro"],
];

// Sprint 56b — Wider centered-modal for the abogado view (kept at
// 1100px on desktop, single-column tabbed under 1200px). The cm-modal
// width override is gated to .abo-cm so it doesn't bleed into other
// CenteredModal instances on the page.
const wideStyle = `
.abo-cm.cm-modal { max-width: min(1100px, 96vw) !important; }
.abo-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 0; height: 100%; }
.abo-cols > section { padding: 24px; overflow: auto; min-width: 0; }
.abo-cols > section.left  { border-right: 1px solid var(--border-hairline); background: var(--bg-page); }
.abo-cols > section.right { background: var(--bg-card); }
@media (max-width: 1199px) {
  .abo-cols { grid-template-columns: 1fr; }
  .abo-cols > section.left  { border-right: 0; border-bottom: 1px solid var(--border-hairline); }
  .abo-tabs { display: flex; gap: 4px; padding: 0 16px; border-bottom: 1px solid var(--border-hairline); background: var(--bg-card); }
  .abo-tab  { padding: 12px 14px; cursor: pointer; color: var(--text-muted); border-bottom: 2px solid transparent; font-size: 13px; }
  .abo-tab.active { color: var(--accent-mint); border-color: var(--accent-mint); font-weight: 500; }
  .abo-cols.tab-left  > section.right { display: none; }
  .abo-cols.tab-right > section.left  { display: none; }
}
@media (min-width: 1200px) {
  .abo-tabs { display: none; }
}
`;

export default function AbogadoCaseDetail({ caseId, open, onClose, onChanged }) {
  const toast = useToast();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("right"); // narrow-screen tab choice
  // feature/carpetas-phase2-docs — P2.1. Multi-role aware: a user
  // who's both an abogado and (carpetas|admin|directora) lands in
  // this view but should still be able to upload carpeta docs.
  // Hardcoding canUpload=false suppressed the hint correctly for
  // pure abogados but also stripped upload rights from multi-role
  // users.
  const carpetaUploadRoles = ["admin", "carpetas", "directora"];
  const userRoles = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
  const canUploadCarpetaDocs = carpetaUploadRoles.some(r => userRoles.includes(r));
  useEffect(() => {
    if (!open || !caseId) return;
    setTab("right"); setData(null);
    casesApi.get(caseId)
      .then(r => setData(r.data))
      .catch((e) => {
        // Sprint 60 — branch on case_deleted vs case_not_found. María's
        // bug: opening a soft-deleted case via stale tab → contestar 404.
        // Now we close the modal and toast the right copy upstream.
        // Z2b — adds case_error_marcado for the admin kill-switch state.
        const code = e?.response?.data?.error;
        const msg  = e?.response?.data?.message;
        if (code === "case_deleted") {
          toast.push(msg || "Este caso fue eliminado y no puede modificarse.", "error");
          onClose?.();
        } else if (code === "case_error_marcado") {
          toast.push(msg || "Este caso fue marcado por error y está fuera del flujo activo.", "error");
          onClose?.();
        } else if (code === "case_not_found") {
          toast.push(msg || "Caso no encontrado", "error");
          onClose?.();
        } else {
          toast.push("No se pudo cargar el caso", "error");
        }
      });
  }, [open, caseId]);

  function refresh() {
    if (!caseId) return;
    casesApi.get(caseId).then(r => setData(r.data));
    onChanged?.();
  }

  if (!open) return null;
  const c = data;

  return (
    <>
      <style>{wideStyle}</style>
      <CenteredModal
        open={open}
        onClose={onClose}
        modalClassName="abo-cm"
        title={c ? c.case_number : "Cargando…"}
        subtitle={c ? `${c.accionante_nombre || "—"} · ${c.juzgado || "—"}` : undefined}
        headerTags={c ? <HeaderTags c={c} showCarpetaBadge={!canUploadCarpetaDocs} /> : null}
        size="xl"
        bodyless
      >
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {c && (
            <>
              <div className="abo-tabs">
                <div className={"abo-tab" + (tab === "left" ? " active" : "")}  onClick={() => setTab("left")}>Insumos recibidos</div>
                <div className={"abo-tab" + (tab === "right" ? " active" : "")} onClick={() => setTab("right")}>Mi trabajo</div>
              </div>
              {/* Tab-agnostic: visible from either column. */}
              <div style={{ padding: "12px 16px 0" }}>
                <ProcesoCarpetasBanner procesoCarpetas={c.proceso_carpetas} />
                {/* P2.2 change 5 — uploaders see full panel; pure
                    lawyers see only the compact CarpetaStatusBadge
                    rendered next to the header pills below. */}
                {canUploadCarpetaDocs && (
                  <CarpetaDocumentsPanel caseId={c.id} canUpload={true} />
                )}
              </div>
              <div className={"abo-cols tab-" + tab} style={{ flex: 1, minHeight: 0 }}>
                <section className="left">
                  <InsumosColumn c={c} onChanged={refresh} />
                </section>
                <section className="right">
                  <TrabajoColumn c={c} onChanged={refresh} onClose={onClose} />
                </section>
              </div>
            </>
          )}
          {!c && (
            <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
          )}
        </div>
      </CenteredModal>
    </>
  );
}

function HeaderTags({ c, showCarpetaBadge }) {
  return (
    <>
      <TipoPill tipo={c.tipo} />
      {c.tipo === "fallo" && <TipoFalloPill value={c.tipo_fallo} />}
      <EstadoPill c={c} />
      <SlaPill c={c} />
      {/* P2.2 change 5 — carpeta status pill for non-uploader viewers. */}
      {showCarpetaBadge && <CarpetaStatusBadge caseId={c.id} />}
    </>
  );
}

function Header({ c, onClose }) {
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>{c.case_number}</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{c.accionante_nombre || "—"} · {c.juzgado || "—"}</div>
      </div>
      <div style={{ display: "flex", gap: 6, marginLeft: 12 }}>
        <TipoPill tipo={c.tipo} />
        {c.tipo === "fallo" && <TipoFalloPill value={c.tipo_fallo} />}
        <EstadoPill c={c} />
        <SlaPill c={c} />
      </div>
      <button className="ghost" style={{ marginLeft: "auto", height: 32, width: 32, padding: 0 }} aria-label="Cerrar" onClick={onClose}>×</button>
    </>
  );
}
function HeaderLoading({ onClose }) {
  return (<>
    <Spinner /> <span style={{ marginLeft: 8 }} className="muted">Cargando…</span>
    <button className="ghost" style={{ marginLeft: "auto" }} onClick={onClose}>×</button>
  </>);
}

// ---------------------------------------------------------------------------
// LEFT — Insumos Recibidos (read-only)
// ---------------------------------------------------------------------------
function InsumosColumn({ c, onChanged }) {
  const ir = c.insumo;

  let banner = null;
  if (c.flag_na && !c.flag_dp && !c.flag_mp && !c.forzar_insumo) {
    banner = { tone: "info",    text: "Caso N/A — sin insumos requeridos." };
  } else if (["pendiente_insumos","insumos_solicitados","expediente_solicitado"].includes(c.estado)) {
    banner = { tone: "warning", text: "Esperando respuesta de Insumos." };
  }

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Insumos recibidos</h2>
      <div className="rule" />

      <dl className="kvs">
        <dt>Caso</dt>      <dd>{c.case_number}</dd>
        <dt>Accionante</dt><dd>
          {/* feature/inline-accionante-edit — primary data-panel dd
              for the abogado view's InsumosColumn. Subtitle stays
              plain per spec. */}
          <InlineEditAccionante c={c} onUpdated={onChanged} />
        </dd>
        {/* feature/tipo-fallo-edit — fallo-only Tipo dd with inline
            edit. Other tipos: AbogadoCaseDetail's TipoPill in
            HeaderTags already shows the value; no dd added there. */}
        {c.tipo === "fallo" && (
          <>
            <dt>Tipo</dt>
            <dd><TipoActuacionDisplayAbo c={c} onChanged={onChanged} /></dd>
          </>
        )}
        <dt>Radicado</dt>  <dd>
          {c.radicado}
          {/* Sprint 73 change 3 — partial-radicado hint. */}
          {c.radicado && !c.proceso_id && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Radicado parcial — proceso no agrupado
            </div>
          )}
        </dd>
        <dt>Juzgado</dt>   <dd>{c.juzgado || "—"}</dd>
        <dt>Término</dt>   <dd>{c.termino_horas != null ? formatTermino(c.termino_horas) : <span className="muted">— (lo establece el abogado)</span>}</dd>
      </dl>

      {banner && (
        <div className={"warn-inline"} style={{
          marginTop: 16,
          background: banner.tone === "info" ? "var(--color-info-bg)" : "var(--color-warning-bg)",
          borderColor: banner.tone === "info" ? "var(--color-info)" : "var(--color-warning)",
          color: banner.tone === "info" ? "var(--color-info)" : "var(--color-warning)",
        }}>
          {banner.text}
        </div>
      )}

      {ir && (
        <div className="card tight" style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong style={{ color: "var(--text-primary)" }}>Solicitud de insumos</strong>
            <span className="badge mint">{ir.solicitud_status || "—"}</span>
            <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>{fmt(ir.fecha_solicitud)}</span>
          </div>
          {ir.generated_pdf_path && (
            <div style={{ marginTop: 8 }}>
              <a href={`${apiBase}/uploads/insumo_pdfs/${ir.generated_pdf_path.split(/[\\/]/).pop()}`} target="_blank" rel="noreferrer">
                Ver PDF de la solicitud
              </a>
            </div>
          )}
        </div>
      )}

    </>
  );
}

// ---------------------------------------------------------------------------
// RIGHT — Mi Trabajo (interactive)
// ---------------------------------------------------------------------------
function TrabajoColumn({ c, onChanged, onClose }) {
  const toast = useToast();
  const { user } = useAuth();
  const editable = ["en_elaboracion", "con_observaciones", "insumos_recibidos"].includes(c.estado);
  const inAuditoria = c.estado === "en_auditoria";
  const isClosed = ["aprobado","en_litisoft","cerrado","fallo_decidido_no_impugnar"].includes(c.estado);

  const lastReject = useMemo(() => {
    // Surface the most recent reject from audit_log when in con_observaciones.
    const log = c.audit_log || [];
    for (let i = log.length - 1; i >= 0; i--) {
      const e = log[i];
      if (e.action === "reject") return e;
    }
    return null;
  }, [c]);

  // ---- Auto-save observaciones ----
  // Sprint X4 — `editable` gate dropped here. Observaciones is private
  // lawyer notes with no workflow dependency, so the textarea accepts
  // input on any open estado. Backend's PUT /:id assigned-abogado branch
  // bypasses the estado-list gate when the only field is observaciones
  // (Sprint X4 backend change), so the auto-save round-trips cleanly
  // even from pendiente_insumos / asignado_abogado / pendiente_archivar.
  // The other gates (audit submit, doc upload) still consume `editable`
  // — those have real workflow meaning and stay untouched.
  const [obs, setObs] = useState(c.observaciones || "");
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  useEffect(() => { setObs(c.observaciones || ""); }, [c.id]);
  function onObsChange(v) {
    setObs(v);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await casesApi.update(c.id, { observaciones: v });
        setSavedAt(Date.now());
      } catch (e) { /* swallow — banner shows below */ }
    }, 800);
  }

  // ---- Set término (requerimiento/desacato/sancion when not yet set) ----
  const needsTerminoFromAbogado =
    c.tipo !== "tutela" && c.tipo !== "fallo" && c.termino_horas == null;
  const [terminoChoice, setTerminoChoice] = useState("");
  const [terminoBusy, setTerminoBusy] = useState(false);
  async function saveTermino() {
    if (!terminoChoice) return;
    setTerminoBusy(true);
    try {
      await casesApi.action(c.id, "set_termino_self", { termino_horas: Number(terminoChoice) });
      toast.push("Término registrado");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al fijar término", "error");
    } finally { setTerminoBusy(false); }
  }

  // ---- Document upload ----
  const docs = c.documents || [];
  const [docType, setDocType] = useState("contestacion");
  const [docFile, setDocFile] = useState(null);
  const [docNotes, setDocNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  async function uploadDoc(e) {
    e.preventDefault();
    if (!docFile) { toast.push("Seleccione un archivo", "error"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("type", docType);
      if (docNotes) fd.append("notes", docNotes);
      await casesApi.uploadDocument(c.id, fd);
      toast.push("Documento subido");
      setDocFile(null); setDocNotes("");
      const el = document.getElementById(`docfile-${c.id}`);
      if (el) el.value = "";
      onChanged?.();
    } catch (err) {
      toast.push(err?.response?.data?.error || "Error al subir", "error");
    } finally { setUploading(false); }
  }
  async function deleteDoc(d) {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await casesApi.deleteDocument(c.id, d.id);
      toast.push("Documento eliminado");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al eliminar", "error");
    }
  }

  // ---- Send to audit ----
  const [confirmSend, setConfirmSend] = useState(false);
  async function sendAudit() {
    try {
      await casesApi.action(c.id, "send_audit");
      toast.push("Caso enviado a auditoría", "success");
      setConfirmSend(false);
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al enviar", "error");
    }
  }

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Mi trabajo</h2>
      <div className="rule" />

      {/* Reject loop banner — most prominent */}
      {c.estado === "con_observaciones" && (
        <div className="warn-inline" style={{
          background: "var(--color-warning-bg)", borderColor: "var(--color-warning)",
          color: "var(--color-warning)", padding: 12,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Auditor solicita correcciones</div>
          <div style={{ color: "var(--text-body)" }}>
            {lastReject?.payload?.observations || lastReject?.notes || "Sin observaciones específicas."}
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {lastReject?.actor_name || "Auditor"} · {fmt(lastReject?.created_at)}
          </div>
        </div>
      )}

      {/* In auditoría — read-only banner */}
      {inAuditoria && (
        <div className="warn-inline" style={{
          background: "var(--color-info-bg)", borderColor: "var(--color-info)",
          color: "var(--color-info)", padding: 12,
        }}>
          <strong>En auditoría</strong> — esperando revisión. No puede editar el caso hasta que el auditor lo revise.
        </div>
      )}

      {/* Closed banner */}
      {isClosed && (
        <div className="warn-inline" style={{
          background: "var(--color-success-bg)", borderColor: "var(--color-success)",
          color: "var(--color-success)", padding: 12,
        }}>
          <strong>Caso {c.estado === "aprobado" ? "aprobado" : c.estado === "en_litisoft" ? "en LITISOFT" : "cerrado"}.</strong>{" "}
          No requiere más acciones del abogado.
        </div>
      )}

      {/* Reparto observations + key info */}
      <div className="card tight" style={{ marginTop: 16 }}>
        <div className="label-uc">Notas de Reparto</div>
        <div style={{ marginTop: 4 }}>{c.observaciones && c.observaciones.trim() ? c.observaciones : <span className="muted">Sin notas.</span>}</div>
      </div>

      {/* Term self-set (only when needed) */}
      {needsTerminoFromAbogado && editable && (
        <div className="card tight" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Establecer término</h3>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Después de revisar el auto, fije el término que aplica.
          </div>
          <div className="row">
            <select value={terminoChoice} onChange={(e) => setTerminoChoice(e.target.value)} style={{ flex: 1 }}>
              <option value="">— elegir —</option>
              {TERMINO_OPTIONS.map(h => <option key={h} value={h}>{formatTermino(h)}</option>)}
            </select>
            <button disabled={!terminoChoice || terminoBusy} onClick={saveTermino}>
              {terminoBusy && <Spinner color="currentColor" />} Guardar
            </button>
          </div>
        </div>
      )}

      {/* Documents */}
      <div className="card tight" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Documentos</h3>
        {!docs.length ? (
          <p className="muted" style={{ margin: 0 }}>Aún no ha subido documentos.</p>
        ) : (
          <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {docs.map(d => (
              <li key={d.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border-hairline)" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{labelDocType(d.type)}</div>
                  <div className="muted" style={{ fontSize: 11 }}>{d.uploaded_by_name || "—"} · {fmt(d.created_at)}</div>
                </div>
                {d.file_path && (
                  <a href={`${apiBase}/uploads/case_documents/${c.id}/${(d.file_path).split(/[\\/]/).pop()}`} target="_blank" rel="noreferrer">Descargar</a>
                )}
                {editable && (
                  <button className="ghost sm" onClick={() => deleteDoc(d)} style={{ color: "var(--color-danger)" }}>Eliminar</button>
                )}
              </li>
            ))}
          </ol>
        )}

        {editable && (
          <form onSubmit={uploadDoc} style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
            <div className="grid-2">
              <div>
                <label>Tipo</label>
                <select value={docType} onChange={(e) => setDocType(e.target.value)}>
                  {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label>Archivo</label>
                <input id={`docfile-${c.id}`} type="file" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <label>Notas (opcional)</label>
              <textarea value={docNotes} onChange={(e) => setDocNotes(e.target.value)} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <button type="submit" disabled={uploading || !docFile}>
                {uploading && <Spinner color="currentColor" />} Subir documento
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Observaciones (auto-save) */}
      <div className="card tight" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Mis observaciones</h3>
          {savedAt && Date.now() - savedAt < 3000 && (
            <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>Guardado</span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Notas internas sobre el caso. Se guarda automáticamente.
        </div>
        {/* Sprint X4 — disabled binding lifted. See comment near useState
            above for full reasoning. */}
        <textarea
          style={{ marginTop: 8 }}
          value={obs}
          onChange={(e) => onObsChange(e.target.value)}
        />
      </div>

      {/* sprint83.2 — mandatory LITISOFT dropdowns inline. Renders above
          the action panel so the lawyer sees + fills before clicking
          "Marcar como contestada". Auto-save on change. */}
      <LitisoftMandatoryInline caseData={c} onSaved={onChanged} />

      {/* feature/lifecycle-redesign. */}
      <div className="card tight" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Acciones del abogado</h3>
        <LawyerActionPanel caseData={c} user={user} onCaseUpdate={onChanged} />
      </div>

      {/* Send to audit — the only submit path (SPEC §11.1 hard rule) */}
      {editable && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border-hairline)" }}>
          <button
            disabled={!docs.length}
            onClick={() => setConfirmSend(true)}
            style={{ width: "100%", height: 44, fontSize: 14 }}
          >
            Enviar a auditoría
          </button>
          {!docs.length && (
            <div className="muted" style={{ fontSize: 12, marginTop: 6, textAlign: "center" }}>
              Suba al menos un documento para poder enviar.
            </div>
          )}
        </div>
      )}

      <Confirm
        open={confirmSend}
        title="Confirmar envío a auditoría"
        message={
          <>
            ¿Confirmar envío? Una vez enviado <strong>no podrá editarlo</strong> hasta que el auditor lo revise.
          </>
        }
        onClose={() => setConfirmSend(false)}
        onConfirm={sendAudit}
        confirmText="Enviar"
      />
    </>
  );
}

function labelDocType(t) {
  return ({
    contestacion: "Contestación",
    memorial: "Memorial",
    sello_radicacion: "Sello de radicación",
    dictamen_junta: "Dictamen de junta",
    auto_juzgado: "Auto del juzgado",
    otro: "Otro",
  })[t] || t;
}

// feature/tipo-fallo-edit — fallo-only display + edit on the
// abogado view's InsumosColumn. Same role gate as CaseDetail
// (PERSONA_EDIT_ROLES). Multi-role abogados also covered (e.g.
// Molly abogado+revisor_fallos).
function TipoActuacionDisplayAbo({ c, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const display = formatTipo(c.tipo, c.sub_tipo, c.no_aplica);
  const userRoles = rolesOf(user);
  const canEdit = c.tipo === "fallo"
    && PERSONA_EDIT_ROLES.some(r => userRoles.includes(r));

  if (!canEdit) return <span>{display}</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span>{display}</span>
      <button
        type="button"
        className="ghost sm"
        style={{ fontSize: 11, padding: "2px 8px" }}
        onClick={() => setOpen(true)}
        title="Editar tipo de actuación"
      >Editar</button>
      {open && (
        <TipoFalloEditModal
          open={open} c={c}
          onClose={() => setOpen(false)}
          onUpdated={onChanged}
        />
      )}
    </span>
  );
}
